// src/utils/deviceFingerprint.js
export const generateDeviceId_kil = () => {
  try {
    // Collect device/browser information
    const fingerprintData = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      plugins: Array.from(navigator.plugins || []).map(p => p.name).join(','),
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      timestamp: Date.now()
    };
    
    console.log('Fingerprint Data:', fingerprintData);
    // Create a string from all properties
    const fingerprintString = Object.values(fingerprintData)
      .filter(value => value !== undefined && value !== null)
      .map(value => String(value))
      .join('|');
    
    // Simple hash function
    const hash = stringToHash(fingerprintString);
    
    // Create base64 encoded device code
    const deviceCode = btoa(JSON.stringify({
      hash: hash,
      timestamp: Date.now(),
      data: {
        userAgent: navigator.userAgent.substring(0, 50),
        platform: navigator.platform
      }
    }));
    
    // Store in localStorage for consistency
    localStorage.setItem('device_fingerprint', hash);
    localStorage.setItem('device_code', deviceCode);
    
    return {
      hash: hash,
      base64: deviceCode,
      rawData: fingerprintData
    };
    
  } catch (error) {
    console.error('Error generating device ID:', error);
    
    // Fallback device ID
    const fallbackHash = 'fallback_' + Math.random().toString(36).substr(2, 9);
    const fallbackCode = btoa(JSON.stringify({
      hash: fallbackHash,
      timestamp: Date.now(),
      isFallback: true
    }));
    
    localStorage.setItem('device_fingerprint', fallbackHash);
    localStorage.setItem('device_code', fallbackCode);
    
    return {
      hash: fallbackHash,
      base64: fallbackCode,
      isFallback: true
    };
  }
};
// src/common/deviceFingerprint.ts
export const generateDeviceId_kil2 = () => {
  // Collect stable device/browser information (no timestamps!)
  const fingerprintData = {
    // Browser/OS Information (relatively stable)
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    
    // Hardware/Display Information (very stable)
    hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
    deviceMemory: (navigator as any).deviceMemory || 'unknown',
    maxTouchPoints: navigator.maxTouchPoints || 0,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1,
    
    // Timezone (stable unless user travels)
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // Browser Features (stable)
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || 'unknown',
    webdriver: navigator.webdriver || false,
    pdfViewerEnabled: (navigator as any).pdfViewerEnabled || false,
    
    // Fonts Detection (very stable - requires font availability check)
    fonts: getAvailableFonts(),
    
    // WebGL Renderer (very stable - GPU fingerprint)
    webglVendor: getWebGLVendor(),
    webglRenderer: getWebGLRenderer(),
    
    // Canvas Fingerprinting (stable)
    canvasFingerprint: getCanvasFingerprint(),
    
    // Audio Fingerprinting (stable)
    audioFingerprint: getAudioFingerprint(),
    
    // Performance API (stable)
    performanceTiming: getPerformanceTiming(),
    
    // Storage Quotas (stable)
    storageQuota: getStorageQuota(),
    
    // Media Devices (stable unless devices change)
    mediaDevices: getMediaDevicesInfo(),
    
    // Battery API (if available)
    batteryInfo: getBatteryInfo(),
    
    // Connection Information (relatively stable)
    connection: getConnectionInfo(),
    
    // Installed Plugins (stable)
    plugins: getPluginsList(),
    
    // MIME Types (stable)
    mimeTypes: getMimeTypes(),
  };

  // // Convert to JSON and create hash
  // const dataString = JSON.stringify(fingerprintData);
  // const hash = simpleHash(dataString);
  // const base64 = btoa(dataString);
  
  // return {
  //   fingerprint: fingerprintData,
  //   hash: hash,
  //   base64: base64
  // };

    // Convert to JSON string (IMPORTANT: same format as backend)
  const dataString = JSON.stringify(fingerprintData, null, 0);
  
  // Generate hash (use either async or sync version)
  const hash = simpleSha256(dataString);
  
  // Encode to base64
  const base64 = btoa(dataString);
  
  return {
    fingerprint: fingerprintData,
    hash: hash,
    base64: base64
  };

  
};

export const generateDeviceId = (userId) => {
  // Core stable identifiers only
  const stableData = {
    // Browser/OS (changes only on browser/OS update)
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    
    // Hardware (never changes)
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth
    },
    
    // Timezone (changes only if user moves)
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // WebGL GPU fingerprint (very stable)
    gpuVendor: getWebGLVendor(),
    gpuRenderer: getWebGLRenderer(),
    
    // Canvas fingerprint (stable)
    canvasHash: getCanvasFingerprint(),
    userId:userId,
    // Available fonts (very stable)
    fonts: getAvailableFonts().slice(0, 5), // Just first 5 for stability
    
    // System language (rarely changes)
    language: navigator.language,
    languages: (navigator as any).languages || [],
    
    // Browser features
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    pdfViewerEnabled: (navigator as any).pdfViewerEnabled,
    
    // Plugin signatures (stable)
    pluginsCount: navigator.plugins?.length || 0,
    mimeTypesCount: navigator.mimeTypes?.length || 0,
  };
  
  const dataString = JSON.stringify(stableData);
  const hash = sha256(dataString);
  const base64 = btoa(dataString);
  
  return {
    fingerprint: stableData,
    hash: hash,
    base64: base64
  };
};
// Helper functions for stable fingerprints:



const getAvailableFonts = (): string[] => {
  // Common fonts to check
  const fonts = [
    'Arial', 'Arial Black', 'Times New Roman', 'Courier New', 
    'Georgia', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Comic Sans MS',
    'Impact', 'Lucida Console', 'Palatino Linotype'
  ];
  
  const availableFonts: string[] = [];
  
  // Create a canvas to test font availability
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  
  if (context) {
    // Base font
    context.font = '72px monospace';
    const baseWidth = context.measureText('mmmmmmmmmm').width;
    
    fonts.forEach(font => {
      context.font = `72px ${font}, monospace`;
      const width = context.measureText('mmmmmmmmmm').width;
      
      // If width is different from monospace, font is available
      if (width !== baseWidth) {
        availableFonts.push(font);
      }
    });
  }
  
  return availableFonts;
};

const getWebGLVendor = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown';
      }
    }
  } catch (e) {
    console.error('WebGL vendor detection failed:', e);
  }
  return 'unknown';
};

const getWebGLRenderer = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      }
    }
  } catch (e) {
    console.error('WebGL renderer detection failed:', e);
  }
  return 'unknown';
};

const getCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'unknown';
    
    canvas.width = 200;
    canvas.height = 50;
    
    // Draw some text
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Browser Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Browser Fingerprint', 4, 17);
    
    // Get image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Simple hash of pixel data
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = ((hash << 5) - hash) + data[i];
      hash = hash & hash;
    }
    
    return hash.toString(16);
  } catch (e) {
    console.error('Canvas fingerprint failed:', e);
    return 'unknown';
  }
};

const getAudioFingerprint = (): string => {
  try {
    // Create an AudioContext and analyze
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContext) return 'no_audio_context';
    
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    
    oscillator.connect(gain);
    gain.connect(context.destination);
    
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;
    
    // Start and immediately stop
    oscillator.start(0);
    oscillator.stop(0.001);
    
    // Get timing info
    const sampleRate = context.sampleRate;
    const maxChannels = context.destination.maxChannelCount;
    
    // Close context
    context.close();
    
    return `sr:${sampleRate},ch:${maxChannels}`;
  } catch (e) {
    console.error('Audio fingerprint failed:', e);
    return 'error';
  }
};

const getPerformanceTiming = () => {
  // Return only static info, not actual timestamps
  return {
    available: !!(window.performance && window.performance.timing)
  };
};

const getStorageQuota = () => {
  if ((navigator as any).storage && (navigator as any).storage.estimate) {
    return 'available';
  }
  return 'unavailable';
};

const getMediaDevicesInfo = (): string => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return 'unavailable';
  }
  
  // Note: This will only show device IDs, not labels (privacy)
  return 'available';
};

const getBatteryInfo = () => {
  if ((navigator as any).getBattery) {
    return 'available';
  }
  return 'unavailable';
};

const getConnectionInfo = () => {
  const connection = (navigator as any).connection || 
                    (navigator as any).mozConnection || 
                    (navigator as any).webkitConnection;
  
  if (connection) {
    return {
      effectiveType: connection.effectiveType || 'unknown'
      // DON'T include downlink or rtt!
    };
  }
  
  return { effectiveType: 'unknown' };
};

const getPluginsList = (): string[] => {
  const plugins = [];
  if (navigator.plugins) {
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name);
    }
  }
  return plugins;
};

const getMimeTypes = (): string[] => {
  const mimeTypes = [];
  if (navigator.mimeTypes) {
    for (let i = 0; i < navigator.mimeTypes.length; i++) {
      mimeTypes.push(navigator.mimeTypes[i].type);
    }
  }
  return mimeTypes;
};

// Simple hash function
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

//  const sha256 = async (message: string): Promise<string> => {
//   // Encode as UTF-8
//   const msgBuffer = new TextEncoder().encode(message);
  
//   // Hash the message
//   const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  
//   // Convert ArrayBuffer to Array
//   const hashArray = Array.from(new Uint8Array(hashBuffer));
  
//   // Convert bytes to hex string
//   return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
// };


const sha256 = async (message: string): Promise<string> => {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};


// Or synchronous fallback
const simpleSha256 = (message: string): string => {
  let hash = 0;
  for (let i = 0; i < message.length; i++) {
    const char = message.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  // Return as hex string (8 characters)
  return Math.abs(hash).toString(16).padStart(8, '0');
};


// Alternative: More stable version without any time-based data

// Hash function
const stringToHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Get stored device ID
export const getStoredDeviceId = () => {
  try {
    const hash = localStorage.getItem('device_fingerprint');
    const code = localStorage.getItem('device_code');
    
    if (!hash || !code) {
      return null;
    }
    
    return {
      hash: hash,
      base64: code
    };
  } catch (error) {
    console.error('Error reading device ID:', error);
    return null;
  }
};

// Clear device ID (for logout)
export const clearDeviceId = () => {
  try {
    localStorage.removeItem('device_fingerprint');
    localStorage.removeItem('device_code');
  } catch (error) {
    console.error('Error clearing device ID:', error);
  }
};